import { supabase } from '../lib/supabase';
import type { ParsedRosterEntry, ImageInput } from '../types';

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-roster`;

export function hasClaudeApiKey(): boolean {
  return true;
}

export async function parseRoster(
  text: string,
  images?: ImageInput[],
  homeBase?: string,
  airlineCode?: string,
  rosterMonth?: string,
  retryCount = 0
): Promise<{ entries: ParsedRosterEntry[]; partial?: boolean; error?: string }> {
  try {
    // Get the current session token
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    if (!accessToken) {
      return { entries: [], error: 'Not logged in. Please sign in and try again.' };
    }

    if (import.meta.env.DEV) {
      console.log('parse-roster: sending POST to', FUNCTION_URL);

      // DEV MOCK: type "mock" to test the globe animation without hitting the API
      if (text.trim().toLowerCase() === 'mock') {
        await new Promise((r) => setTimeout(r, 2000)); // simulate network delay
        return {
          entries: [
            { type: 'flight' as const, date: '2026-03-05', flightNumber: 'EK203', departureAirport: 'DXB', arrivalAirport: 'FRA', departureTime: '09:00', arrivalTime: '13:30', airlineCode: 'EK' },
            { type: 'flight' as const, date: '2026-03-07', flightNumber: 'EK204', departureAirport: 'FRA', arrivalAirport: 'DXB', departureTime: '15:00', arrivalTime: '23:30', airlineCode: 'EK' },
            { type: 'off' as const, date: '2026-03-08', endDate: '2026-03-09' },
            { type: 'flight' as const, date: '2026-03-10', flightNumber: 'EK384', departureAirport: 'DXB', arrivalAirport: 'BKK', departureTime: '08:30', arrivalTime: '18:45', airlineCode: 'EK' },
            { type: 'flight' as const, date: '2026-03-12', flightNumber: 'EK385', departureAirport: 'BKK', arrivalAirport: 'DXB', departureTime: '23:50', arrivalTime: '03:15', airlineCode: 'EK' },
            { type: 'flight' as const, date: '2026-03-14', flightNumber: 'EK001', departureAirport: 'DXB', arrivalAirport: 'LHR', departureTime: '07:45', arrivalTime: '12:00', airlineCode: 'EK' },
            { type: 'flight' as const, date: '2026-03-15', flightNumber: 'EK002', departureAirport: 'LHR', arrivalAirport: 'DXB', departureTime: '21:00', arrivalTime: '07:00', airlineCode: 'EK' },
            { type: 'flight' as const, date: '2026-03-18', flightNumber: 'EK412', departureAirport: 'DXB', arrivalAirport: 'SYD', departureTime: '21:30', arrivalTime: '17:45', airlineCode: 'EK' },
            { type: 'flight' as const, date: '2026-03-20', flightNumber: 'EK413', departureAirport: 'SYD', arrivalAirport: 'DXB', departureTime: '21:15', arrivalTime: '05:00', airlineCode: 'EK' },
            { type: 'flight' as const, date: '2026-03-23', flightNumber: 'EK521', departureAirport: 'DXB', arrivalAirport: 'BOM', departureTime: '10:00', arrivalTime: '14:45', airlineCode: 'EK' },
            { type: 'flight' as const, date: '2026-03-24', flightNumber: 'EK522', departureAirport: 'BOM', arrivalAirport: 'DXB', departureTime: '04:00', arrivalTime: '06:00', airlineCode: 'EK' },
            { type: 'standby' as const, date: '2026-03-26', note: '05:00-14:00' },
            { type: 'flight' as const, date: '2026-03-28', flightNumber: 'EK073', departureAirport: 'DXB', arrivalAirport: 'JFK', departureTime: '10:15', arrivalTime: '15:30', airlineCode: 'EK' },
            { type: 'flight' as const, date: '2026-03-30', flightNumber: 'EK074', departureAirport: 'JFK', arrivalAirport: 'DXB', departureTime: '22:30', arrivalTime: '18:45', airlineCode: 'EK' },
          ],
        };
      }
    }

    const response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ text, images, homeBase, airlineCode, rosterMonth }),
    });

    const data = await response.json();

    if (!response.ok) {
      const msg = data?.error || `Server error (${response.status})`;

      if (retryCount < 2 && (response.status >= 500 || response.status === 401)) {
        const delay = (retryCount + 1) * 2000;
        await new Promise((r) => setTimeout(r, delay));
        return parseRoster(text, images, homeBase, airlineCode, rosterMonth, retryCount + 1);
      }

      return { entries: data?.entries ?? [], error: msg };
    }

    if (data?.error) {
      return { entries: data.entries ?? [], error: data.error };
    }

    if (!data?.entries || data.entries.length === 0) {
      return { entries: [], error: 'No entries found. Make sure you pasted a roster with flights or duties.' };
    }

    return {
      entries: data.entries as ParsedRosterEntry[],
      partial: data.partial === true,
    };
  } catch (err) {
    console.error('parse-roster fetch error:', err);
    console.error('parse-roster error details:', err instanceof Error ? err.message : String(err));

    if (retryCount < 2) {
      const delay = (retryCount + 1) * 1500;
      await new Promise((r) => setTimeout(r, delay));
      return parseRoster(text, images, homeBase, airlineCode, rosterMonth, retryCount + 1);
    }

    return {
      entries: [],
      error: `Failed to connect: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}
